import { Check, ChevronDown } from 'lucide-react'
import type * as React from 'react'

import { cn } from '../lib/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu'

export interface SelectMenuOption<T extends string> {
  value: T
  /** Already-translated display text — this package stays i18n-agnostic. */
  label: string
}

export interface SelectMenuProps<T extends string> {
  value: T
  onValueChange: (value: T) => void
  options: readonly SelectMenuOption<T>[]
  /** Accessible name for the control, e.g. "Sort recipes". Not shown. */
  label: string
  /** Leading icon on the trigger (e.g. an ArrowUpDown). */
  icon?: React.ReactNode
  /** Quiet prefix before the selected value, e.g. "Sort:". Hidden below `sm`. */
  prefix?: string
  /** Hide the selected value below `sm`, leaving an icon-only trigger. */
  compact?: boolean
  align?: 'start' | 'center' | 'end'
  /** Extra classes for the trigger. */
  className?: string
  /** Extra classes for the menu panel. */
  contentClassName?: string
}

// A single-select menu: a pill trigger that echoes the current choice, over a menu of
// mutually exclusive options. Distinct from a bare DropdownMenu, which is a list of
// commands — this one has a *value*, so its options are `menuitemradio` and carry
// aria-checked. A check mark alone conveys the selection to sighted users only.
//
// Keyboard behavior comes from DropdownMenu (arrows, type-ahead, Home/End, Enter/Space,
// Escape, and the APG Tab handling), so it is correct here by construction.
export function SelectMenu<T extends string>({
  value,
  onValueChange,
  options,
  label,
  icon,
  prefix,
  compact = false,
  align = 'end',
  className,
  contentClassName
}: SelectMenuProps<T>) {
  const selected = options.find((o) => o.value === value)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            // h-11 matches SearchField, so a toolbar's controls line up and both clear the
            // 44px touch target.
            'inline-flex h-11 shrink-0 items-center gap-2 rounded-full border-[1.5px] border-border bg-card px-3.5 text-sm font-light tracking-wide text-foreground transition-colors',
            'hover:border-brand-green-border',
            'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:focus-visible:ring-pelorous-300',
            'sm:gap-2.5 sm:px-4',
            className
          )}>
          {icon}
          {prefix && <span className="hidden font-medium text-faint sm:inline">{prefix}</span>}
          <span className={cn(compact && 'hidden sm:inline')}>{selected?.label}</span>
          <ChevronDown size={12} className="shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={cn('min-w-[190px] border-border bg-card p-2 text-foreground sm:min-w-[210px]', contentClassName)}>
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => onValueChange(option.value)}
            // The selection is mutually exclusive, so these are radios, not plain commands.
            role="menuitemradio"
            aria-checked={option.value === value}
            // es-green-7 is a DARK green in dark mode — meant for light surfaces — so on the
            // dark menu panel the selected label came out at 1.72:1, well under the 4.5:1 AA
            // needs for text. es-green-5 is the ramp's light green: 6.84:1 there.
            className="cursor-pointer gap-2.5 rounded-[10px] py-2 text-sm font-light tracking-wide focus:bg-block-bg data-[active=true]:font-semibold data-[active=true]:text-brand-green-ink dark:data-[active=true]:text-brand-green-ink"
            data-active={option.value === value}>
            {option.label}
            {option.value === value && <Check size={14} className="ml-auto text-brand-green-ink" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
